"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { getSetSummaries } from "../lib/opIndex";

const PAGE_ID_PREFIX = "sets";

interface SetCategory {
  id: string;
  label: string;
  match: (code: string) => boolean;
}

const CATEGORIES: SetCategory[] = [
  { id: "op", label: "Booster Packs", match: (c) => /^OP\d/i.test(c) },
  { id: "st", label: "Starter Decks", match: (c) => /^ST\d/i.test(c) },
  { id: "eb", label: "Extra Boosters", match: (c) => /^EB\d/i.test(c) },
  { id: "prb", label: "Premium Boosters", match: (c) => /^PRB\d/i.test(c) },
  { id: "other", label: "Other Products", match: () => true },
];

function categorize(code: string): string {
  for (const cat of CATEGORIES) {
    if (cat.id !== "other" && cat.match(code)) return cat.id;
  }
  return "other";
}

export default function SetsPage() {
  const [query, setQuery] = useState("");

  const allSummaries = useMemo(() => getSetSummaries(), []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allSummaries;
    return allSummaries.filter(
      (s) =>
        s.code.toLowerCase().includes(q) ||
        s.name.toLowerCase().includes(q),
    );
  }, [allSummaries, query]);

  const grouped = useMemo(() => {
    const map = new Map<string, typeof filtered>();
    for (const cat of CATEGORIES) {
      map.set(cat.id, []);
    }
    for (const s of filtered) {
      const catId = categorize(s.code);
      map.get(catId)!.push(s);
    }
    return map;
  }, [filtered]);

  const activeCategories = CATEGORIES.filter(
    (cat) => (grouped.get(cat.id)?.length ?? 0) > 0,
  );

  const scrollTo = (sectionId: string) => {
    const el = document.getElementById(`${PAGE_ID_PREFIX}-section-${sectionId}`);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      id={`${PAGE_ID_PREFIX}-page`}
      className="flex min-h-screen flex-col bg-slate-950 text-slate-50"
    >
      <Header idPrefix={`${PAGE_ID_PREFIX}-header`} />

      <main
        id={`${PAGE_ID_PREFIX}-main`}
        className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-4 px-4 py-5"
      >
        <section
          id={`${PAGE_ID_PREFIX}-list-section`}
          className="space-y-4 rounded-xl border border-slate-800 bg-slate-900/70 p-4 text-sm"
        >
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2
                id={`${PAGE_ID_PREFIX}-list-title`}
                className="text-sm font-semibold text-slate-100"
              >
                All sets
              </h2>
              <p className="text-xs text-slate-400">
                Showing{" "}
                <span
                  id={`${PAGE_ID_PREFIX}-list-count`}
                  className="font-semibold text-slate-100"
                >
                  {filtered.length}
                </span>{" "}
                sets
              </p>
            </div>
            <input
              id={`${PAGE_ID_PREFIX}-search-input`}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by name or code..."
              className="w-full rounded-full border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/40 md:w-64"
            />
          </div>

          {/* Jump-to navigation */}
          <div
            id={`${PAGE_ID_PREFIX}-jump-nav`}
            className="flex flex-wrap gap-2"
          >
            {activeCategories.map((cat) => (
              <button
                key={cat.id}
                id={`${PAGE_ID_PREFIX}-jump-${cat.id}`}
                type="button"
                onClick={() => scrollTo(cat.id)}
                className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1 text-xs text-slate-200 transition-colors hover:border-blue-500/50 hover:bg-slate-800 hover:text-blue-400"
              >
                {cat.label} ({grouped.get(cat.id)?.length ?? 0})
              </button>
            ))}
          </div>
        </section>

        {/* Category sections */}
        {activeCategories.map((cat) => {
          const sets = grouped.get(cat.id) ?? [];
          return (
            <section
              key={cat.id}
              id={`${PAGE_ID_PREFIX}-section-${cat.id}`}
              className="scroll-mt-24 space-y-3 rounded-xl border border-slate-800 bg-slate-900/70 p-4 text-sm"
            >
              <h3 className="text-sm font-semibold text-slate-100">
                {cat.label}
              </h3>
              <div className="overflow-x-auto rounded-lg border border-slate-800">
                <table className="min-w-full divide-y divide-slate-800 text-xs">
                  <thead className="bg-slate-900/80">
                    <tr>
                      <th className="w-12 px-2 py-2"></th>
                      <th className="px-3 py-2 text-left font-medium text-slate-300">Name</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-300">Code</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-300">Unique cards</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-300">Release date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800 bg-slate-950">
                    {sets.map((s) => (
                      <tr
                        key={s.code}
                        id={`${PAGE_ID_PREFIX}-table-row-${s.code}`}
                        className="hover:bg-slate-900/70"
                      >
                        <td className="px-2 py-1.5">
                          {s.image ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                              src={s.image}
                              alt={s.code}
                              className="h-8 w-8 rounded object-cover"
                            />
                          ) : (
                            <div className="h-8 w-8 rounded bg-slate-800" />
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <Link
                            id={`${PAGE_ID_PREFIX}-set-link-${s.code}`}
                            href={`/search?set=${encodeURIComponent(s.code)}`}
                            className="text-blue-400 hover:underline"
                          >
                            {s.name}
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-slate-200">{s.code}</td>
                        <td className="px-3 py-2 text-slate-200">{s.uniqueCardCount}</td>
                        <td className="px-3 py-2 text-slate-400">{s.releaseDate ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })}
      </main>

      <Footer idPrefix={`${PAGE_ID_PREFIX}-footer`} />
    </div>
  );
}
